import { Component, computed, effect, inject, input, OnDestroy, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ICampaignForm } from '@campaign-form/campaign-form.interface';
import { CampaignFormStore } from '@campaign-form/campaign-form.store';
import { CampaignCustomerComponent } from '@campaign-form/form-fields/campaign-basics/campaign-customer/campaign-customer.component';
import { CampaignDatesComponent } from '@campaign-form/form-fields/campaign-basics/campaign-dates/campaign-dates.component';
import { CampaignFinanceComponent } from '@campaign-form/form-fields/campaign-basics/campaign-finance/campaign-finance.component';
import { CampaignNotesComponent } from '@campaign-form/form-fields/campaign-basics/campaign-notes/campaign-notes.component';
import { CampaignTeamSelectComponent } from '@campaign-form/form-fields/campaign-basics/campaign-team-select/campaign-team-select.component';
import { CampaignTeamComponent } from '@campaign-form/form-fields/campaign-basics/campaign-team/campaign-team.component';
import { ITeam } from '@core/interfaces/team.interface';
import { IObjectPermission } from '@core/interfaces/user.interface';
import { PermissionService } from '@core/services/permission.service';
import { ECampaignStatus, ECurrency } from '@enums/campaign.enum';
import { EPermissions } from '@enums/permissions.enum';
import { TCampaignApi } from '@pages/campaigns/campaigns.interface';
import { ICustomer } from '@pages/customers/customers.interface';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { endOfDay, startOfDay } from 'date-fns';
import { debounceTime, Subscription } from 'rxjs';

@Component({
  imports: [
    CampaignCustomerComponent,
    CampaignDatesComponent,
    CampaignFinanceComponent,
    CampaignNotesComponent,
    CampaignTeamComponent,
    CampaignTeamSelectComponent,
  ],
  selector: 'app-campaign-basics',
  templateUrl: './campaign-basics.component.html',
})
export class CampaignBasicsComponent implements OnDestroy {
  public readonly campaign = input.required<TCampaignApi | null>();
  public readonly getValues = input.required<boolean>();

  public readonly canSave = output<boolean>();
  public readonly changed = output<boolean>();
  public readonly values = output<FormGroup<ICampaignForm>['value']>();

  public readonly formInitialized = signal<boolean>(false);

  public readonly campaignFormStore = inject(CampaignFormStore);
  private readonly permissionService = inject(PermissionService);

  private readonly currentUserPermissions = toSignal(this.permissionService.currentUserPermissions$, {
    initialValue: this.permissionService.currentUserPermissions,
  });

  public readonly canReadCustomers = computed(() =>
    this.currentUserPermissions().includes(EPermissions.customerReadOwn),
  );
  public readonly canCreateCustomer = computed(() =>
    this.currentUserPermissions().includes(EPermissions.customerCreate),
  );
  public readonly canReadTeams = computed(() => this.currentUserPermissions().includes(EPermissions.teamReadOwn));
  public readonly canCreateTeam = computed(() => this.currentUserPermissions().includes(EPermissions.teamCreate));

  /** The field only earns its place in the form when there's something useful to do with it -
   * either an existing client to pick, or the ability to create the first one. Decided here
   * (rather than read off the child) so the parent can decide whether to mount
   * `<app-campaign-customer>` at all - an unmounted child never instantiates its host element,
   * which is what keeps this flex-col's `gap` from inserting spacing for a field that has nothing
   * to show (see `showTeamSelectField` below for the identical reasoning on the "Team" field). */
  public readonly showCustomerField = computed<boolean>(
    () =>
      this.canReadCustomers() && ((this.campaignFormStore.customers$()?.length ?? 0) > 0 || this.canCreateCustomer()),
  );

  /** Same reasoning as `showCustomerField` above, for the "Team" field. */
  public readonly showTeamSelectField = computed<boolean>(
    () => this.canReadTeams() && ((this.campaignFormStore.teams$()?.length ?? 0) > 0 || this.canCreateTeam()),
  );

  public form: FormGroup<ICampaignForm> = new FormGroup<ICampaignForm>({
    budget: new FormControl<string | null>(null),
    currency: new FormControl<ECurrency | null>(ECurrency.eur),
    customer: new FormControl<ICustomer | null>(null),
    customerId: new FormControl<string | null>(null),
    endDate: new FormControl<Date | null>(null, [Validators.required]),
    endTime: new FormControl<Date | null>(null, [Validators.required]),
    name: new FormControl('', [Validators.required]),
    notes: new FormControl('', [Validators.maxLength(4096)]),
    startDate: new FormControl<Date | null>(null, [Validators.required]),
    startTime: new FormControl<Date | null>(null, [Validators.required]),
    team: new FormControl<ITeam | null>(null),
    teamId: new FormControl<string | null>(null),
    whitelist: new FormControl<IObjectPermission[] | null>([]),
  });

  private readonly dateUtils = inject(DateUtilitiesService);

  private formSubscription: Subscription | null = null;

  constructor() {
    this.createInitializeEffect();
    this.createGetValuesEffect();
    this.createFetchEffects();
  }

  public ngOnDestroy(): void {
    this.formSubscription?.unsubscribe();
  }

  private createInitializeEffect(): void {
    effect(() => {
      const campaign = this.campaign();

      if (!campaign) {
        return;
      }

      this.initialize();
    });
  }

  /** Triggers the customer/team fetches here rather than in each child field, since those children
   * only mount once `showCustomerField()`/`showTeamSelectField()` is true - which, for a user who
   * can read but not create, depends on the fetch having already resolved. Left in the child, the
   * fetch would never fire because the child that starts it would never mount. */
  private createFetchEffects(): void {
    effect(() => {
      if (this.canReadCustomers()) {
        this.campaignFormStore.fetchCustomers();
      }
    });

    effect(() => {
      if (this.canReadTeams()) {
        this.campaignFormStore.fetchTeams();
      }
    });
  }

  private initialize(): void {
    this.recreateForm();
    this.changed.emit(false);
    this.fillForm();
    this.createFormSubscription();
  }

  private fillForm(): void {
    const localEndDate = this.campaign()?.end
      ? this.dateUtils.parseDateTimeFromApi(this.campaign()?.end as string)
      : endOfDay(new Date());
    const localStartDate = this.campaign()?.start
      ? this.dateUtils.parseDateTimeFromApi(this.campaign()?.start as string)
      : startOfDay(new Date());

    this.form.patchValue({
      budget: this.campaign()?.budget ? String(this.campaign()?.budget) : null,
      currency: this.campaign()?.currency ?? ECurrency.eur,
      customer: this.campaign()?.customer,
      customerId: this.campaign()?.customerId,
      endDate: localEndDate,
      endTime: localEndDate,
      name: this.campaign()?.name,
      notes: this.campaign()?.notes,
      startDate: localStartDate,
      startTime: localStartDate,
      team: this.campaign()?.team,
      teamId: this.campaign()?.teamId,
      whitelist: this.campaign()?.whitelist ?? [],
    });

    if (this.campaign()?.status !== ECampaignStatus.draft) {
      this.form.controls.currency.disable();
    }

    this.formInitialized.set(true);
  }

  private createFormSubscription(): void {
    this.formSubscription?.unsubscribe();

    this.formSubscription = this.form.valueChanges.pipe(debounceTime(100)).subscribe(() => {
      this.changed.emit(this.form.invalid || this.form.dirty);
      this.canSave.emit(this.form.valid);
    });
  }

  private createGetValuesEffect(): void {
    effect(() => {
      const getValues = this.getValues();

      if (!getValues) {
        return;
      }

      this.form.markAllAsTouched();

      if (this.form.valid) {
        this.values.emit(this.form.getRawValue());
      }
    });
  }

  private recreateForm(): void {
    this.form = new FormGroup<ICampaignForm>({
      budget: new FormControl<string | null>(null),
      currency: new FormControl<ECurrency | null>(ECurrency.eur),
      customer: new FormControl<ICustomer | null>(null),
      customerId: new FormControl<string | null>(null),
      endDate: new FormControl<Date | null>(null, [Validators.required]),
      endTime: new FormControl<Date | null>(null, [Validators.required]),
      name: new FormControl('', [Validators.required]),
      notes: new FormControl('', [Validators.maxLength(4096)]),
      startDate: new FormControl<Date | null>(null, [Validators.required]),
      startTime: new FormControl<Date | null>(null, [Validators.required]),
      team: new FormControl<ITeam | null>(null),
      teamId: new FormControl<string | null>(null),
      whitelist: new FormControl<IObjectPermission[] | null>([]),
    });

    this.createFormSubscription();
  }
}
